import React, { useState, useEffect } from 'react';
import './Form.css';
import { useUser } from '../contexts/UserContext';
import { useEditInitiative } from '../contexts/EditInitiativeContext';
import { initiativeService } from '../services/api';
import TextEditorModal from './TextEditorModal';
import IdeaCheckModal from './IdeaCheckModal';
import axios from 'axios';
import { api } from '../config';

type EditableFieldType = 'purpose' | 'value' | 'solution_scope' | 'proposed_workflow' | 'inputs_and_data_sources';

const SubmitInitiative: React.FC = () => {
  const { user, division } = useUser();
  const { editData, setEditData, switchToHomeTab, setSuccessMessage } = useEditInitiative();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isResubmitting, setIsResubmitting] = useState(false);
  const [resubmittingId, setResubmittingId] = useState<number | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingField, setEditingField] = useState<EditableFieldType | null>(null);
  const [showIdeaCheck, setShowIdeaCheck] = useState(false);
  const [ideaCheckDone, setIdeaCheckDone] = useState(false);
  const [selectedDivision, setSelectedDivision] = useState('');
  const [divisionReviewers, setDivisionReviewers] = useState<{ email: string; display_name: string | null }[]>([]);
  const [reviewersLoading, setReviewersLoading] = useState(false);
  const [notifyApprovers, setNotifyApprovers] = useState(true);
  const [formData, setFormData] = useState({
    use_case_name: '',
    proposed_solution_owner: '',
    solution_sponsor: '',
    purpose: '',
    value: '',
    users: '',
    solution_scope: '',
    proposed_workflow: '',
    inputs_and_data_sources: '',
  });

  // Load edit data if resubmitting a rejected initiative
  useEffect(() => {
    if (editData) {
      setFormData({
        use_case_name: editData.use_case_name || '',
        proposed_solution_owner: editData.proposed_solution_owner || '',
        solution_sponsor: editData.solution_sponsor || '',
        purpose: editData.purpose || '',
        value: editData.value || '',
        users: editData.users || '',
        solution_scope: editData.solution_scope || '',
        proposed_workflow: editData.proposed_workflow || '',
        inputs_and_data_sources: editData.inputs_and_data_sources || '',
      });
      setIsResubmitting(true);
      setResubmittingId((editData as any).id || null);
      setIdeaCheckDone(true); // skip idea check for resubmissions
      setMessage({ type: 'success', text: 'Form pre-filled with your previous submission. Make changes and resubmit.' });
    } else {
      // Show idea check modal for fresh new submissions
      setShowIdeaCheck(true);
      setIdeaCheckDone(false);
    }
  }, [editData]);

  // Pre-populate division from user's saved division
  useEffect(() => {
    if (division) setSelectedDivision(division);
  }, [division]);

  // Fetch reviewers when division changes
  useEffect(() => {
    if (!selectedDivision) { setDivisionReviewers([]); return; }
    setReviewersLoading(true);
    axios.get(api('/api/reviewers/list'))
      .then(res => {
        const all: { email: string; display_name: string | null; division: string | null }[] = res.data;
        setDivisionReviewers(all.filter(r => r.division === selectedDivision));
      })
      .catch(() => setDivisionReviewers([]))
      .finally(() => setReviewersLoading(false));
  }, [selectedDivision]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const openEditor = (field: EditableFieldType) => {
    setEditingField(field);
    setEditorOpen(true);
  };

  const saveEditorText = (text: string) => {
    if (editingField) {
      setFormData(prev => ({
        ...prev,
        [editingField]: text,
      }));
    }
    setEditorOpen(false);
    setEditingField(null);
  };

  const cancelEditor = () => {
    setEditorOpen(false);
    setEditingField(null);
  };

  const getFieldLabel = (field: EditableFieldType): string => {
    const labels: Record<EditableFieldType, string> = {
      purpose: 'Purpose',
      value: 'Value/Benefits',
      solution_scope: 'Solution Scope',
      proposed_workflow: 'Proposed Workflow',
      inputs_and_data_sources: 'Inputs and Data Sources',
    };
    return labels[field];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all required fields (trim whitespace)
    if (!formData.use_case_name.trim()) {
      setMessage({ type: 'error', text: 'Use case name is required' });
      return;
    }
    if (!formData.proposed_solution_owner.trim()) {
      setMessage({ type: 'error', text: 'Proposed solution owner is required' });
      return;
    }
    if (!formData.solution_sponsor.trim()) {
      setMessage({ type: 'error', text: 'Solution sponsor is required' });
      return;
    }
    if (!formData.purpose.trim()) {
      setMessage({ type: 'error', text: 'Purpose is required' });
      return;
    }
    if (!formData.value.trim()) {
      setMessage({ type: 'error', text: 'Value is required' });
      return;
    }
    if (!formData.users.trim()) {
      setMessage({ type: 'error', text: 'Users is required' });
      return;
    }
    if (!formData.solution_scope.trim()) {
      setMessage({ type: 'error', text: 'Solution scope is required' });
      return;
    }
    if (!formData.proposed_workflow.trim()) {
      setMessage({ type: 'error', text: 'Proposed workflow is required' });
      return;
    }
    if (!formData.inputs_and_data_sources.trim()) {
      setMessage({ type: 'error', text: 'Inputs and data sources is required' });
      return;
    }

    setIsSubmitting(true);
    setMessage(null);

    try {
      if (isResubmitting && resubmittingId) {
        // Resubmitting a rejected initiative - treat as new submission
        await initiativeService.submitInitiative({
          ...formData,
          submitted_by: user?.email || 'unknown',
          division: selectedDivision || undefined,
          notify_approvers: notifyApprovers,
        } as any);
        
        // Set success message in context and navigate to home
        setSuccessMessage('AI Initiative resubmitted successfully!');
        
        // Clear form and edit data
        setFormData({
          use_case_name: '',
          proposed_solution_owner: '',
          solution_sponsor: '',
          purpose: '',
          value: '',
          users: '',
          solution_scope: '',
          proposed_workflow: '',
          inputs_and_data_sources: '',
        });
        setIsResubmitting(false);
        setResubmittingId(null);
        setEditData(null);
        
        // Navigate to home tab
        setTimeout(() => {
          switchToHomeTab();
        }, 500);
      } else {
        // New submission
        await initiativeService.submitInitiative({
          ...formData,
          submitted_by: user?.email || 'unknown',
          division: selectedDivision || undefined,
          notify_approvers: notifyApprovers,
        } as any);
        setMessage({ type: 'success', text: 'AI Initiative submitted successfully!' });

        // Set success message in context and navigate to home
        setSuccessMessage('AI Initiative submitted successfully!');

        // Clear form
        setFormData({
          use_case_name: '',
          proposed_solution_owner: '',
          solution_sponsor: '',
          purpose: '',
          value: '',
          users: '',
          solution_scope: '',
          proposed_workflow: '',
          inputs_and_data_sources: '',
        });

        // Navigate to home tab after 2 seconds
        setTimeout(() => {
          switchToHomeTab();
        }, 2000);
      }
    } catch (error: any) {
      console.error('Error submitting initiative:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to submit initiative. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if ((showIdeaCheck && !ideaCheckDone) || !ideaCheckDone) {
    return (
      <div className="idea-check-page">
        <IdeaCheckModal
          onProceed={(prefill) => {
            setShowIdeaCheck(false);
            setIdeaCheckDone(true);
            if (prefill && Object.keys(prefill).length > 0) {
              setFormData(prev => ({
                ...prev,
                use_case_name: prefill.use_case_name || prev.use_case_name,
                purpose: prefill.purpose || prev.purpose,
                value: prefill.value || prev.value,
                solution_scope: prefill.solution_scope || prev.solution_scope,
                proposed_workflow: prefill.proposed_workflow || prev.proposed_workflow,
                inputs_and_data_sources: prefill.inputs_and_data_sources || prev.inputs_and_data_sources,
                users: prefill.users || prev.users,
              }));
            }
          }}
          onCancel={() => { switchToHomeTab(); }}
        />
      </div>
    );
  }

  return (
    <div className="form-container">
      {isSubmitting && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="spinner"></div>
            <p>Submitting your AI Initiative...</p>
            <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>This may take a moment...</p>
          </div>
        </div>
      )}
      
      {message && (
        <div className={`${message.type}-message`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Row 1: Use Case Name + Proposed Solution Owner */}
        <div className="form-grid">
          <div className="form-group required">
            <label htmlFor="use_case_name">Use Case Name</label>
            <input
              id="use_case_name"
              type="text"
              name="use_case_name"
              value={formData.use_case_name}
              onChange={handleInputChange}
              placeholder="Enter the use case name"
              required
            />
          </div>

          <div className="form-group required">
            <label htmlFor="proposed_solution_owner">Proposed Solution Owner</label>
            <input
              id="proposed_solution_owner"
              type="text"
              name="proposed_solution_owner"
              value={formData.proposed_solution_owner}
              onChange={handleInputChange}
              placeholder="Person responsible for coordination"
              required
            />
          </div>
        </div>

        {/* Row 2: Solution Sponsor + Users */}
        <div className="form-grid">
          <div className="form-group required">
            <label htmlFor="solution_sponsor">Solution Sponsor</label>
            <input
              id="solution_sponsor"
              type="text"
              name="solution_sponsor"
              value={formData.solution_sponsor}
              onChange={handleInputChange}
              placeholder="Responsible functional leader"
              required
            />
          </div>

          <div className="form-group required">
            <label htmlFor="users">Users</label>
            <textarea
              id="users"
              name="users"
              value={formData.users}
              onChange={handleInputChange}
              placeholder="Who are the users of this solution?"
              required
            />
          </div>
        </div>

        {/* Row 3: Purpose (Full Width) */}
        <div className="form-grid full">
          <div className="form-group required">
            <label htmlFor="purpose">
              Purpose
              <button
                type="button"
                className="link-button"
                onClick={() => openEditor('purpose')}
                title="Open full editor"
              >
                (expand)
              </button>
            </label>
            <textarea
              id="purpose"
              name="purpose"
              value={formData.purpose}
              onChange={handleInputChange}
              placeholder="What is the problem that this solution solves?"
              required
            />
          </div>
        </div>

        {/* Row 4: Value + Solution Scope */}
        <div className="form-grid">
          <div className="form-group required">
            <label htmlFor="value">
              Value
              <button
                type="button"
                className="link-button"
                onClick={() => openEditor('value')}
                title="Open full editor"
              >
                (expand)
              </button>
            </label>
            <textarea
              id="value"
              name="value"
              value={formData.value}
              onChange={handleInputChange}
              placeholder="What value does this solution bring?"
              required
            />
          </div>

          <div className="form-group required">
            <label htmlFor="solution_scope">
              Solution Scope
              <button
                type="button"
                className="link-button"
                onClick={() => openEditor('solution_scope')}
                title="Open full editor"
              >
                (expand)
              </button>
            </label>
            <textarea
              id="solution_scope"
              name="solution_scope"
              value={formData.solution_scope}
              onChange={handleInputChange}
              placeholder="Describe what is in scope and out of scope"
              required
            />
          </div>
        </div>

        {/* Row 5: Proposed Workflow + Inputs and Data Sources */}
        <div className="form-grid">
          <div className="form-group required">
            <label htmlFor="proposed_workflow">
              Proposed Workflow
              <button
                type="button"
                className="link-button"
                onClick={() => openEditor('proposed_workflow')}
                title="Open full editor"
              >
                (expand)
              </button>
            </label>
            <textarea
              id="proposed_workflow"
              name="proposed_workflow"
              value={formData.proposed_workflow}
              onChange={handleInputChange}
              placeholder="Describe the proposed workflow of the solution"
              required
            />
          </div>

          <div className="form-group required">
            <label htmlFor="inputs_and_data_sources">
              Inputs and Data Sources
              <button
                type="button"
                className="link-button"
                onClick={() => openEditor('inputs_and_data_sources')}
                title="Open full editor"
              >
                (expand)
              </button>
            </label>
            <textarea
              id="inputs_and_data_sources"
              name="inputs_and_data_sources"
              value={formData.inputs_and_data_sources}
              onChange={handleInputChange}
              placeholder="List the inputs and data sources needed"
              required
            />
          </div>
        </div>

        <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <input
            type="checkbox"
            id="notifyApprovers"
            checked={notifyApprovers}
            onChange={(e) => setNotifyApprovers(e.target.checked)}
            style={{ cursor: 'pointer', width: '18px', height: '18px' }}
          />
          <label htmlFor="notifyApprovers" style={{ cursor: 'pointer', margin: 0, flex: 1 }}>
            <strong>Notify approvers</strong>
            <span style={{ display: 'block', fontSize: '12px', color: '#666', marginTop: '2px' }}>
              When checked, division reviewers will be notified of this submission via email
            </span>
          </label>
        </div>

        <div className="button-group">
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit Initiative'}
          </button>
        </div>

        {/* Approvers panel */}
        <div className="approvers-panel">
          <div className="approvers-panel__header">
            <div className="approvers-panel__title">Approvers{selectedDivision ? ` — ${selectedDivision}` : ''}</div>
          </div>
          {selectedDivision && (
            <div className="approvers-panel__body">
              {reviewersLoading && <span className="approvers-panel__loading">Loading...</span>}
              {!reviewersLoading && divisionReviewers.length === 0 && (
                <span className="approvers-panel__none">No reviewers assigned to <strong>{selectedDivision}</strong> yet.</span>
              )}
              {!reviewersLoading && divisionReviewers.length > 0 && (
                <>
                  <p className="approvers-panel__note">
                    Your submission will be sent to the reviewers below. <strong>Any one of them can approve.</strong>
                  </p>
                  <div className="approvers-panel__list">
                    {divisionReviewers.map(r => (
                      <div key={r.email} className="approvers-panel__reviewer">
                        <span className="approvers-panel__reviewer-avatar">
                          {(r.display_name || r.email).charAt(0).toUpperCase()}
                        </span>
                        <a className="approvers-panel__reviewer-name" href={`mailto:${r.email}`} title={r.email}>
                          {r.display_name || r.email}
                        </a>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </form>

      <TextEditorModal
        isOpen={editorOpen}
        title={`Edit ${editingField ? getFieldLabel(editingField as EditableFieldType) : ''}`}
        value={editingField ? formData[editingField as keyof typeof formData] : ''}
        onSave={saveEditorText}
        onCancel={cancelEditor}
      />
    </div>
  );
};

export default SubmitInitiative;
